import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import * as XLSX from "xlsx"
import { toast } from "react-toastify"
import { PGET, POST } from "../../../helpers/api"

// Oy nomlari (Iyul, Avgust, ...)
const MONTHS = [
	"Yanvar", "Fevral", "Mart", "Aprel", "May", "Iyun",
	"Iyul", "Avgust", "Sentabr", "Oktabr", "Noyabr", "Dekabr",
]

// Raqamni "2 600 000" ko'rinishida formatlash
const fmt = (n) => String(Math.round(Number(n) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, " ")

// Bajarilish foizi (reja 0 bo'lsa — null)
const percentOf = (plan, fact) => (plan > 0 ? Math.round((fact / plan) * 100) : null)

// Foizga qarab rang
const colorOf = (p) => {
	if (p === null) return { text: "#98a2b3", bar: "#e4e7ec", chip: "#f2f4f7" }
	if (p >= 90) return { text: "#067647", bar: "#12b76a", chip: "#ecfdf3" }
	if (p >= 60) return { text: "#b54708", bar: "#f79009", chip: "#fffaeb" }
	return { text: "#b42318", bar: "#f04438", chip: "#fef3f2" }
}

function Index() {
	const { t } = useTranslation()

	const now = new Date()
	const [month, setMonth] = useState(now.getMonth() + 1) // 1..12
	const [year, setYear] = useState(now.getFullYear())
	const [rows, setRows] = useState([])
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)

	const years = useMemo(() => {
		const y = now.getFullYear()
		return [y - 2, y - 1, y, y + 1]
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Ma'lumotlarni API'dan olish
	async function getData() {
		setLoading(true)
		try {
			// GET /services/admin/api/plan-fact?year=2026&month=7
			const res = await PGET("/services/admin/api/plan-fact", { year, month }, { loader: true })
			const data = Array.isArray(res?.data) ? res.data : res?.data?.data
			setRows(
				(data || []).map((r) => ({
					id: r.dealerId ?? r.id,
					name: r.name ?? r.fullName ?? "",
					plan: Number(r.plan) || 0,
					fact: Number(r.fact) || 0,
				}))
			)
		} catch (e) {
			setRows([])
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		getData()
	}, [month, year]) // eslint-disable-line react-hooks/exhaustive-deps

	// Reja qiymatini tahrirlash
	function onPlanChange(id, value) {
		const num = Number(String(value).replace(/\D/g, "")) || 0
		setRows((prev) => prev.map((r) => (r.id === id ? { ...r, plan: num } : r)))
	}

	// Rejalarni saqlash
	async function save() {
		setSaving(true)
		try {
			// POST /services/admin/api/plan-fact — helper shows the success toast on 200
			const res = await POST("/services/admin/api/plan-fact", {
				year,
				month,
				items: rows.map((r) => ({ dealerId: r.id, plan: r.plan })),
			}, { loader: true })
			if (!res || res.status !== 200) {
				toast.error(t("Xatolik yuz berdi"))
			}
		} catch (e) {
			toast.error(t("Xatolik yuz berdi"))
		} finally {
			setSaving(false)
		}
	}

	// Excelga eksport
	function exportExcel() {
		const excelData = rows.map((r, i) => {
			const p = percentOf(r.plan, r.fact)
			return {
				"№": i + 1,
				Diler: r.name,
				"Reja (so'm)": r.plan,
				"Fakt (so'm)": r.fact,
				Bajarilishi: p === null ? "—" : `${p}%`,
			}
		})
		const ws = XLSX.utils.json_to_sheet(excelData)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, "Plan-Fakt")
		XLSX.writeFile(wb, `plan-fakt_${year}-${String(month).padStart(2, "0")}.xlsx`)
	}

	const totals = useMemo(() => {
		const plan = rows.reduce((s, r) => s + (Number(r.plan) || 0), 0)
		const fact = rows.reduce((s, r) => s + (Number(r.fact) || 0), 0)
		return { plan, fact, percent: percentOf(plan, fact) }
	}, [rows])

	return (
		<div className="plan-fact">
			{/* Sarlavha + boshqaruv */}
			<div className="pf-header">
				<div>
					<h4 className="pf-title">Plan / Fakt</h4>
					<div className="pf-subtitle">{t("Dilerlar kesimida oylik savdo rejasi va bajarilishi")}</div>
				</div>
				<div className="pf-controls">
					<select className="pf-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
						{MONTHS.map((m, i) => (
							<option key={i} value={i + 1}>{t(m)}</option>
						))}
					</select>
					<select className="pf-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
						{years.map((y) => (
							<option key={y} value={y}>{y}</option>
						))}
					</select>
					<button className="pf-btn pf-btn-outline" onClick={exportExcel} disabled={loading}>
						Excel
					</button>
					<button className="pf-btn pf-btn-primary" onClick={save} disabled={saving || loading}>
						{saving ? t("Saqlanmoqda...") : t("Saqlash")}
					</button>
				</div>
			</div>

			{/* Jadval */}
			<div className="pf-card">
				<div className="pf-row pf-row-head">
					<div className="pf-col-name">{t("Diler")}</div>
					<div className="pf-col-num">{t("Reja (so'm)")}</div>
					<div className="pf-col-num">{t("Fakt (so'm)")}</div>
					<div className="pf-col-bar">{t("Bajarilishi")}</div>
				</div>

				{loading ? (
					<div className="pf-empty">{t("Yuklanmoqda...")}</div>
				) : rows.length === 0 ? (
					<div className="pf-empty">{t("LIST_IS_EMPTY")}</div>
				) : (
					rows.map((r, i) => {
						const p = percentOf(r.plan, r.fact)
						const c = colorOf(p)
						return (
							<div className="pf-row" key={r.id}>
								<div className="pf-col-name">
									<span className="pf-index">{i + 1}.</span> {r.name}
								</div>
								<div className="pf-col-num">
									<input
										type="text"
										className="pf-input"
										value={r.plan ? fmt(r.plan) : ""}
										placeholder="0"
										onChange={(e) => onPlanChange(r.id, e.target.value)}
									/>
								</div>
								<div className="pf-col-num pf-fact">{fmt(r.fact)}</div>
								<div className="pf-col-bar">
									<span className="pf-chip" style={{ background: c.chip, color: c.text }}>
										{p === null ? "—" : `${p}%`}
									</span>
									<div className="pf-progress">
										<div
											className="pf-progress-fill"
											style={{ width: `${p === null ? 0 : Math.min(p, 100)}%`, background: c.bar }}
										/>
									</div>
								</div>
							</div>
						)
					})
				)}

				{/* Jami */}
				{rows.length > 0 && (
					<div className="pf-row pf-row-total">
						<div className="pf-col-name">{t("Jami")}:</div>
						<div className="pf-col-num">{fmt(totals.plan)}</div>
						<div className="pf-col-num">{fmt(totals.fact)}</div>
						<div className="pf-col-bar">
							<span
								className="pf-chip"
								style={{ background: colorOf(totals.percent).chip, color: colorOf(totals.percent).text }}
							>
								{totals.percent === null ? "—" : `${totals.percent}%`}
							</span>
						</div>
					</div>
				)}
			</div>

			<div className="pf-note">
				{t("«Fakt» ustuni yangi POS'larning birinchi tarif to'lovidan avtomatik hisoblanadi, «Reja» esa har bir oy uchun alohida saqlanadi. Reja kiritilmagan qatorda foiz «—» ko'rinadi.")}
			</div>

			<style jsx>{`
				.plan-fact {
					padding: 4px;
				}
				.pf-header {
					display: flex;
					justify-content: space-between;
					align-items: flex-start;
					flex-wrap: wrap;
					gap: 16px;
					margin-bottom: 18px;
				}
				.pf-title {
					font-size: 24px;
					font-weight: 700;
					color: #101828;
					margin: 0 0 4px;
				}
				.pf-subtitle {
					font-size: 14px;
					color: #667085;
				}
				.pf-controls {
					display: flex;
					align-items: center;
					gap: 10px;
					flex-wrap: wrap;
				}
				.pf-select {
					height: 42px;
					border: 1px solid #e4e7ec;
					border-radius: 10px;
					padding: 0 14px;
					background: #fff;
					color: #344054;
					font-size: 14px;
					cursor: pointer;
					outline: none;
					min-width: 92px;
				}
				.pf-select:focus {
					border-color: #12b76a;
				}
				.pf-btn {
					height: 42px;
					padding: 0 22px;
					border-radius: 999px;
					font-size: 14px;
					font-weight: 600;
					cursor: pointer;
					border: 1px solid transparent;
					transition: all 0.15s ease;
				}
				.pf-btn:disabled {
					opacity: 0.6;
					cursor: not-allowed;
				}
				.pf-btn-outline {
					background: #fff;
					border-color: #12b76a;
					color: #12b76a;
				}
				.pf-btn-outline:hover:not(:disabled) {
					background: #ecfdf3;
				}
				.pf-btn-primary {
					background: #12b76a;
					color: #fff;
				}
				.pf-btn-primary:hover:not(:disabled) {
					background: #0ea15c;
				}
				.pf-card {
					background: #fff;
					border: 1px solid #eef0f3;
					border-radius: 16px;
					box-shadow: 0 1px 3px rgba(16, 24, 40, 0.04);
					overflow: hidden;
				}
				.pf-row {
					display: grid;
					grid-template-columns: 2fr 1.4fr 1.2fr 1.6fr;
					align-items: center;
					gap: 16px;
					padding: 18px 24px;
					border-top: 1px solid #f2f4f7;
				}
				.pf-row:first-child {
					border-top: none;
				}
				.pf-row-head {
					background: #fcfcfd;
					color: #667085;
					font-size: 13px;
					font-weight: 600;
				}
				.pf-col-name {
					font-size: 15px;
					color: #101828;
				}
				.pf-index {
					color: #98a2b3;
				}
				.pf-col-num {
					text-align: right;
				}
				.pf-row-head .pf-col-num {
					text-align: right;
				}
				.pf-fact {
					font-size: 15px;
					color: #344054;
					font-weight: 500;
				}
				.pf-input {
					width: 100%;
					max-width: 180px;
					height: 42px;
					border: 1px solid #e4e7ec;
					border-radius: 10px;
					padding: 0 14px;
					text-align: right;
					font-size: 15px;
					color: #101828;
					outline: none;
					margin-left: auto;
					display: block;
				}
				.pf-input:focus {
					border-color: #12b76a;
					box-shadow: 0 0 0 3px rgba(18, 183, 106, 0.12);
				}
				.pf-col-bar {
					display: flex;
					align-items: center;
					gap: 12px;
				}
				.pf-row-head .pf-col-bar {
					display: block;
				}
				.pf-chip {
					flex: 0 0 auto;
					min-width: 52px;
					text-align: center;
					padding: 5px 12px;
					border-radius: 999px;
					font-size: 13px;
					font-weight: 700;
				}
				.pf-progress {
					flex: 1;
					height: 8px;
					background: #f2f4f7;
					border-radius: 999px;
					overflow: hidden;
				}
				.pf-progress-fill {
					height: 100%;
					border-radius: 999px;
					transition: width 0.4s ease;
				}
				.pf-row-total {
					background: #f9fafb;
					font-weight: 700;
				}
				.pf-row-total .pf-col-name,
				.pf-row-total .pf-col-num {
					color: #101828;
					font-size: 15px;
				}
				.pf-empty {
					padding: 40px;
					text-align: center;
					color: #98a2b3;
				}
				.pf-note {
					margin-top: 16px;
					font-size: 13px;
					color: #98a2b3;
					line-height: 1.5;
				}
				@media (max-width: 768px) {
					.pf-row {
						grid-template-columns: 1.4fr 1fr;
						row-gap: 8px;
					}
					.pf-col-bar {
						grid-column: 1 / -1;
					}
					.pf-row-head {
						display: none;
					}
				}
			`}</style>
		</div>
	)
}

export default Index
